import { api } from '@/api/client';
//import type { Transaction, TransactionApiResponse } from '@/pages/merchant/Transactions/types/transaction';

// Webhook API endpoints - using transactions endpoint
const WEBHOOK_ENDPOINTS = {
  WEBHOOKS: '/merchant/v1/webhooks/',
  RETRY: (id: string) => `/merchant/v1/webhook/retry/?transaction_id=${id}/`,
  FILTER: (id: string, merchantTransactionId: string) => `/merchant/v1/webhook/?transaction_id=${id}&merchant_transaction_id=${merchantTransactionId}/`,
  FILTER_STATUS: '/merchant/v1/webhooks/',
  // DELETE: (id: string) => `/merchant/v1/transactions/${id}/`,
  // STATS: '/merchant/v1/transactions/stats/',
  // EXPORT: '/merchant/v1/transactions/export/',
} as const;

class WebhookService {

  /**
   * Get all webhooks with optional filters and pagination
   * Now fetches from transactions endpoint and maps to webhook format
   */
  async getWebhookTransactions(page: number = 1, pageSize: number = 10): Promise<any> {
    try {
      const url = `${WEBHOOK_ENDPOINTS.WEBHOOKS}`;
      const response = await api.get<any>(url, {
        params: {
          page: page,
          page_size: pageSize,
        },
      });
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transactions');
    }
  }

  /**
   * Get webhook by ID
   * Now fetches from transactions endpoint and maps to webhook format
   */
  async getWebhookById(id: string, merchantTransactionId?: string): Promise<any> {
    try {
      let url;

      if (id && merchantTransactionId) {
        // Case 1: Both transaction_id and merchant_transaction_id
        url = `${WEBHOOK_ENDPOINTS.WEBHOOKS}?transaction_id=${id}&merchant_transaction_id=${merchantTransactionId}`;
      } else if (id) {
        // Case 3: Only transaction_id
        url = `${WEBHOOK_ENDPOINTS.WEBHOOKS}?transaction_id=${id}`;
      } else if (merchantTransactionId) {
        // Case 2: Only merchant_transaction_id
        url = `${WEBHOOK_ENDPOINTS.WEBHOOKS}?merchant_transaction_id=${merchantTransactionId}`;
      } else {
        throw new Error('Either transaction_id or merchant_transaction_id must be provided');
      }

      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      console.error(`Error fetching webhook ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get webhook by ID
   * Now fetches from transactions endpoint and maps to webhook format
   */
  async getRetryWebhookById(id: string): Promise<any> {
    try {
      const url = `${WEBHOOK_ENDPOINTS.RETRY(id)}`;
      const response = await api.post<{ data: any }>(
        url,
        { transaction_id: id }
      );
      return response;
    } catch (error) {
      console.error(`Error fetching webhook ${id}:`, error);
      throw error;
    }
  }


  /**
 * Get single transaction by ID
 */
  async getWebhookStatusData(status?: string): Promise<any> {
    try {
      if (!status) {
        throw new Error('Either status must be provided');
      }

      const queryParams = new URLSearchParams();

      if (status) {
        queryParams.append('webhook_status', status);
      }

      const url = `${WEBHOOK_ENDPOINTS.FILTER_STATUS}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }


  /**
* Get date range
*/
  async getWebhookDateRangeData(startDate?: any, endDate?: any): Promise<any> {
    try {
      if (!startDate && !endDate) {
        throw new Error('Either Start Date and End Date must be provided');
      }

      const queryParams = new URLSearchParams();

      if (startDate) {
        queryParams.append('start_date', startDate);
      }

      if (endDate) {
        queryParams.append('end_date', endDate);
      }

      const url = `${WEBHOOK_ENDPOINTS.FILTER_STATUS}?${queryParams.toString()}`;
      const response = await api.get<{ data: any }>(url);
      return response;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch transaction');
    }
  }

  // /**
  //  * Create a new webhook
  //  */
  // async createWebhook(data: CreateWebhookRequest): Promise<Webhook> {
  //   try {
  //     const response = await api.post<Webhook>(WEBHOOK_ENDPOINTS.WEBHOOKS, data);
  //     return response.data;
  //   } catch (error) {
  //     console.error('Error creating webhook:', error);
  //     throw error;
  //   }
  // }

  // /**
  //  * Update an existing webhook
  //  */
  // async updateWebhook(id: string, data: UpdateWebhookRequest): Promise<Webhook> {
  //   try {
  //     const response = await api.put<Webhook>(WEBHOOK_ENDPOINTS.WEBHOOK_BY_ID(id), data);
  //     return response.data;
  //   } catch (error) {
  //     console.error(`Error updating webhook ${id}:`, error);
  //     throw error;
  //   }
  // }

  // /**
  //  * Delete a webhook
  //  */
  // async deleteWebhook(id: string): Promise<void> {
  //   try {
  //     await api.delete(WEBHOOK_ENDPOINTS.WEBHOOK_BY_ID(id));
  //   } catch (error) {
  //     console.error(`Error deleting webhook ${id}:`, error);
  //     throw error;
  //   }
  // }

  // /**
  //  * Retry a failed webhook
  //  * Now uses transactions endpoint for retry functionality
  //  */
  // async retryWebhook(id: string, forceRetry?: boolean): Promise<Webhook> {
  //   try {
  //     const response = await api.post<Transaction>(WEBHOOK_ENDPOINTS.RETRY_WEBHOOK(id), {
  //       transaction_id: id,
  //       force_retry: forceRetry,
  //     });
  //     return mapTransactionToWebhook(response.data);
  //   } catch (error) {
  //     console.error(`Error retrying webhook ${id}:`, error);
  //     throw error;
  //   }
  // }

  // /**
  //  * Get webhook metrics/statistics
  //  * Now uses transactions endpoint for metrics
  //  */
  // async getWebhookMetrics(): Promise<{
  //   totalCount: number;
  //   deliveredCount: number;
  //   failedCount: number;
  //   pendingCount: number;
  //   retryCount: number;
  // }> {
  //   try {
  //     const response = await api.get<{
  //       totalCount: number;
  //       deliveredCount: number;
  //       failedCount: number;
  //       pendingCount: number;
  //       retryCount: number;
  //     }>(WEBHOOK_ENDPOINTS.WEBHOOK_METRICS);
  //     return response.data;
  //   } catch (error) {
  //     console.error('Error fetching webhook metrics:', error);
  //     throw error;
  //   }
  // }

  // /**
  //  * Export webhooks data
  //  * Now uses transactions endpoint for export functionality
  //  */
  // async exportWebhooks(format: 'csv' | 'excel' = 'csv', filters?: WebhookFilters): Promise<Blob> {
  //   try {
  //     const response = await api.get(WEBHOOK_ENDPOINTS.WEBHOOKS, {
  //       params: {
  //         export: format,
  //         search: filters?.searchTerm,
  //         status: filters?.statusFilter,
  //         transaction_status: filters?.transactionStatus,
  //       },
  //       responseType: 'blob',
  //     });
  //     return response as unknown as Blob;
  //   } catch (error) {
  //     console.error('Error exporting webhooks:', error);
  //     throw error;
  //   }
  // }

  /**
 * Handle API errors
 */
  private handleError(error: unknown, defaultMessage: string): Error {
    if (error && typeof error === 'object' && 'message' in error) {
      return error as Error;
    }
    return new Error(defaultMessage);
  }
}

// Create singleton instance
export const webhookService = new WebhookService();
export default webhookService;
